/**
 * Approvals — type-aware store + shared primitives. Verbatim port of the
 * canonical screens-approvals.jsx data layer.
 *
 * Content types that can require approval each resolve their item from the
 * owning store so the queue + review screen render any type generically.
 * NOTE: item resolvers are null-safe — where a store isn't module-exported
 * yet the title falls back to the item id (logged; wiring tightens as the
 * stores are surfaced). Persisted to localStorage with migration from the
 * old events-only shape.
 */
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import React from "react";
import { Icon, Pill, T } from "@v0y4ge/jrni-ui";
import {
  approvalsGetSettings, approvalsUpdateSettings, approvalsListProcesses,
  approvalsCreateProcess, approvalsUpdateProcess, approvalsDeleteProcess,
  approvalsListSubmissions, approvalsSubmitForApproval, approvalsMarkUnderReview,
  approvalsDecideSubmission, approvalsResetSubmission,
} from "@v0y4ge/api-client";
import { toast } from "../../shell/toast";
import { can, getUser, onTenantChange, onTenantSwitching } from "../../shell/access";
import { logUpsert, logDelete } from "../../lib/audit";
import type { NavApi } from "../../shell/router";
import { ServicesStore } from "../services/services-data";
import { moduleKnownAbsent } from "../../lib/tenant-modules";
import { useT, t as i18nT } from "../../lib/i18n";

export const APPROVAL_STATUS: Record<string, { label: string; labelKey: string; tone: string; dot: boolean; icon: string }> = {
  draft:          { labelKey: "approvals.store.notSubmitted", get label() { return i18nT("approvals.store.notSubmitted", "Not submitted"); },  tone: "neutral", dot: false, icon: "edit" },
  submitted:      { labelKey: "approvals.store.submitted", get label() { return i18nT("approvals.store.submitted", "Submitted"); },      tone: "info",    dot: true,  icon: "send" },
  "under-review": { labelKey: "approvals.store.underReview", get label() { return i18nT("approvals.store.underReview", "Under review"); },   tone: "info",    dot: true,  icon: "eye" },
  feedback:       { labelKey: "approvals.store.feedbackGiven", get label() { return i18nT("approvals.store.feedbackGiven", "Feedback given"); }, tone: "warn",    dot: true,  icon: "msg" },
  approved:       { labelKey: "approvals.store.approved", get label() { return i18nT("approvals.store.approved", "Approved"); },       tone: "success", dot: true,  icon: "check" },
  rejected:       { labelKey: "approvals.store.rejected", get label() { return i18nT("approvals.store.rejected", "Rejected"); },       tone: "danger",  dot: true,  icon: "x" },
};

let _apSeq = 0;
export const apUid = (p: string) => `${p}_${Date.now().toString(36)}${(_apSeq++).toString(36)}`;

export interface ApprovalTypeDef {
  id: string; label: string; labelKey: string; noun: string; icon: string;
  getItem: (id: string) => Record<string, any> | null;
  title: (it: Record<string, any> | null) => string | undefined;
  open: (nav: NavApi, id: string) => void;
  setStatus?: (id: string, st: string) => void;
}
export const APPROVAL_TYPES: ApprovalTypeDef[] = [
  { id: "events", labelKey: "approvals.store.events", get label() { return i18nT("approvals.store.events", "Events"); }, noun: "event", icon: "tags",
    getItem: () => null, title: (it) => it?.title, open: (nav, id) => nav.go("event.public", { eventId: id }) },
  { id: "campaigns", labelKey: "approvals.store.campaigns", get label() { return i18nT("approvals.store.campaigns", "Campaigns"); }, noun: "campaign", icon: "send",
    getItem: () => null, title: (it) => it?.name, open: (nav) => nav.go("campaigns.approvals", { tab: "approval" }) }, // (CC, 2026-10-05) campaigns.email is unregistered; the registered approvals tab is the real destination
  { id: "surveys", labelKey: "approvals.store.surveys", get label() { return i18nT("approvals.store.surveys", "Surveys"); }, noun: "survey", icon: "csv",
    getItem: () => null, title: (it) => it?.name, open: (nav, id) => nav.go("surveys.edit", { id, view: 1 }) },
  { id: "questions", labelKey: "approvals.store.bookingQuestions", get label() { return i18nT("approvals.store.bookingQuestions", "Booking questions"); }, noun: "booking question", icon: "help",
    getItem: () => null, title: (it) => it?.label, open: (nav, id) => nav.go("questions.edit", { id, view: 1 }) },
  { id: "question-groups", labelKey: "approvals.store.questionGroups", get label() { return i18nT("approvals.store.questionGroups", "Question groups"); }, noun: "question group", icon: "layers",
    getItem: () => null, title: (it) => it?.name, open: (nav, id) => nav.go("questions.group.edit", { id, view: 1 }) },
  { id: "promos", labelKey: "approvals.store.couponsPromos", get label() { return i18nT("approvals.store.couponsPromos", "Coupons & promos"); }, noun: "coupon / promo", icon: "tags",
    getItem: () => null, title: (it) => it?.name, open: (nav, id) => nav.go("promos.edit", { id, view: 1 }) },
  { id: "services", labelKey: "approvals.store.services", get label() { return i18nT("approvals.store.services", "Services"); }, noun: "service", icon: "layers",
    getItem: (id) => ServicesStore.getService(id), title: (it) => it?.name, open: (nav, id) => nav.go("services.edit", { id, view: 1 }),
    setStatus: (id, st) => ServicesStore.setServiceStatus(id, st === "approved" ? "published" : "draft") },
];
export const APPROVAL_TYPE = (id: string): ApprovalTypeDef => APPROVAL_TYPES.find((t) => t.id === id) || APPROVAL_TYPES[0];
/** The approval types a tenant is offered (CC, 2026-10-04). Services only where a service can exist, i.e. the
 *  tenant has booking or a queue (the staff list's rule); the server's own type list omits it there too.
 *  Unknown modules (loading, or the read failed) keep every type. */
export function approvalTypesFor(mods: string[] | null): ApprovalTypeDef[] {
  const noServices = moduleKnownAbsent("booking", mods) && moduleKnownAbsent("queue", mods);
  return noServices ? APPROVAL_TYPES.filter((t) => t.id !== "services") : APPROVAL_TYPES;
}
export const apItemTitle = (type: string, id: string) => { const t = APPROVAL_TYPE(type); const it = t.getItem(id); return (it && t.title(it)) || id; };

export interface ApprovalProcess { id: string; type: string; name: string; description: string; requireAll: boolean; status: string; approvers: { id: string; name: string; email: string }[]; }
export interface ApprovalFeedback { id: string; at: string; by: string; text?: string; attachments?: { type: string; name: string }[]; decision?: string; }
export interface ApprovalSubmission { type: string; itemId: string; processId: string; status: string; submittedBy: string; submittedAt: string; history: { status: string; at: string; by?: string }[]; feedback: ApprovalFeedback[]; }

function _apSeed() {
  const enabledTypes: Record<string, boolean> = {};
  APPROVAL_TYPES.forEach((t) => (enabledTypes[t.id] = false)); // all off until an admin turns them on
  return {
    enabledTypes,
    processes: [] as ApprovalProcess[], // #635: demo approval processes removed — served via approvalsListProcesses / @v0y4ge/api-mocks
    submissions: {} as Record<string, ApprovalSubmission>, // #635: demo submissions removed — served via approvalsListSubmissions / @v0y4ge/api-mocks
    activity: [] as Record<string, any>[],
  };
}
type ApState = ReturnType<typeof _apSeed>;

/* Memory cache hydrated from the approvals contract ops; mutations call the
 * typed client AWAITED and only then update the cache + notify (no-fake-
 * interactivity mandate — the old localStorage-only write path is gone). */
let s: ApState | null = null;
/* The Approvals switch lives in lib/approvals-flag.ts (CC 0224); re-exported here for
   the screens that already import the store. */
import { APPROVALS_COMING_SOON } from "../../lib/approvals-flag";
export { APPROVALS_COMING_SOON };

const subs = new Set<() => void>();
const _key = (type: string, id: string) => `${type}:${id}`;
type AnyFn = (...a: unknown[]) => Promise<unknown>;
const _op = (fn: unknown): AnyFn | null => (typeof fn === "function" ? fn as AnyFn : null);
let _hydrated = false;
function hydrate() {
  if (APPROVALS_COMING_SOON) return;
  if (_hydrated) return;
  _hydrated = true;
  load();
  (async () => {
    try {
      const [settings, procs, subsRes] = await Promise.all([
        _op(approvalsGetSettings)?.() ?? null,
        _op(approvalsListProcesses)?.() ?? null,
        _op(approvalsListSubmissions)?.() ?? null,
      ]);
      const st = settings as Record<string, any> | null;
      if (st?.enabledTypes) s!.enabledTypes = { ...s!.enabledTypes, ...st.enabledTypes };
      const pRaw = procs as Record<string, any> | null;
      const pRows: any[] = pRaw?.processes ?? pRaw?.data ?? (Array.isArray(procs) ? procs as any[] : []);
      if (pRows.length) s!.processes = pRows as ApprovalProcess[];
      const sbRaw = subsRes as Record<string, any> | null;
      const sbRows: any[] = sbRaw?.submissions ?? sbRaw?.data ?? (Array.isArray(subsRes) ? subsRes as any[] : []);
      if (sbRows.length) {
        const next: Record<string, ApprovalSubmission> = {};
        sbRows.forEach((v: any) => { if (v?.type && v?.itemId) next[_key(v.type, v.itemId)] = v as ApprovalSubmission; });
        s!.submissions = next;
      }
      subs.forEach((f) => f());
    } catch (e) { console.warn("approvals hydrate failed:", e); }
  })();
}
const load = (): ApState => { if (!s) s = _apSeed(); hydrate(); return s; };
const notify = () => { subs.forEach((f) => f()); };
// Cross-tenant leak fix (docket 2026-07-27, item 1): purge SYNCHRONOUSLY the
// instant a switch starts (`_hydrated = true` BLOCKS an incidental load()
// call — load() always seeds an empty `s` first, so nothing unsafe renders
// — during the pending window); (re)hydrate only once the tenant is CONFIRMED.
onTenantSwitching(() => { s = null; _hydrated = true; notify(); });
// Capability gate (docket 2026-07-31): this eager re-hydrate issued the 3
// approvals/* prefetch 403s for every persona. Gated on the SAME any-of pair
// the Approvals rail item and AppLauncher tile already use.
//
// Deliberately NOT gating hydrate()/load() themselves: this store has a second
// consumer with a different audience — CreationShell decides whether the event
// wizard shows its Approvals step from enabledTypes()/processes("events"), and
// CreateEventApprovals renders the submission from it. Those are Event Manager
// surfaces, and their reads run through load(), which still hydrates on demand.
// Gating the store outright would silently drop the wizard step.
onTenantChange(() => {
  _hydrated = false;
  if (!APPROVALS_COMING_SOON && (can("settings.write") || can("campaigns.approve"))) load();
  else if (!s) s = _apSeed(); // keep state seeded (what load() would have done) without fetching
  notify();
});
const _nowTime = () => { try { const d = new Date(); return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; } catch { return "now"; } };
const _actor = () => { const u = getUser(); return { name: u.name || "You", init: u.init || (u.name || "You").split(" ").map((w) => w[0]).join("").slice(0, 2), color: u.color || T.p1, email: u.email || "" }; };
/* Record one approval action to the runtime activity trail. The audit-log
 * screen reads auditEntries(). */
function _log(entry: { type: string; itemId: string; action: string; severity?: string; notifTitle?: string; icon?: string; tone?: string }) {
  load();
  const tm = APPROVAL_TYPE(entry.type);
  const title = apItemTitle(entry.type, entry.itemId);
  const row = { id: apUid("act"), at: "just now", day: "Today", time: _nowTime(), actor: _actor(), type: entry.type, itemId: entry.itemId, action: entry.action, target: `${tm.label} · ${title}`, severity: entry.severity || "info" };
  s!.activity = [row, ...(s!.activity || [])].slice(0, 200);
}

export const ApprovalsStore = {
  /* ── enablement (type-aware) ── */
  enabledTypes: () => ({ ...load().enabledTypes }),
  isEnabled: (type: string) => !!load().enabledTypes[type],
  enabled: () => !!load().enabledTypes.events,
  setEnabled: async (type: string, v: boolean) => {
    load();
    const next = { ...s!.enabledTypes, [type]: !!v };
    try {
      await _op(approvalsUpdateSettings)?.({ enabledTypes: next });
      s!.enabledTypes = next; notify();
    } catch (e) {
      console.warn("approvalsUpdateSettings failed:", e);
      toast({ tone: "danger", title: i18nT("approvals.store.couldnTUpdateApprovals", "Couldn't update approvals"), subtitle: i18nT("approvals.store.theSettingWasNotChangedRetryInAMoment", "The setting was not changed. Retry in a moment.") });
    }
  },
  anyEnabled: () => Object.values(load().enabledTypes).some(Boolean),
  /* ── go-live gate ── */
  isBlocked: (type: string, id: string) => { if (!load().enabledTypes[type]) return false; const sub = s!.submissions[_key(type, id)]; return !(sub && sub.status === "approved"); },
  blockReason: (type: string, id: string) => { if (!load().enabledTypes[type]) return ""; const sub = s!.submissions[_key(type, id)]; const noun = APPROVAL_TYPE(type).noun; if (!sub) return `This ${noun} must be submitted and approved before it can go live.`; if (sub.status === "approved") return ""; if (sub.status === "rejected") return `This ${noun} was rejected — address the feedback and resubmit.`; return `This ${noun} is awaiting approval — it can't go live until it's approved.`; },
  /* ── activity / audit ── */
  activity: () => load().activity.slice(),
  auditEntries: () => load().activity.map((a) => ({ id: a.id, day: a.day || "Today", time: a.time, actor: a.actor, action: a.action, category: "Approvals", target: a.target, ip: "—", severity: a.severity || "info" })),
  /* ── processes ── */
  processes: (type?: string) => { const all = load().processes.slice(); return type ? all.filter((p) => p.type === type) : all; },
  getProcess: (id: string) => load().processes.find((p) => p.id === id) || null,
  upsertProcess: async (p: ApprovalProcess): Promise<ApprovalProcess> => {
    load();
    const exists = s!.processes.some((x) => x.id === p.id);
    // Awaited client write first; cache + notify only on success.
    if (exists) await _op(approvalsUpdateProcess)?.(p.id, p);
    else await _op(approvalsCreateProcess)?.(p);
    const i = s!.processes.findIndex((x) => x.id === p.id);
    logUpsert("approvalProcess", i >= 0 ? (s!.processes[i] as unknown as Record<string, unknown>) : null, p as unknown as Record<string, unknown>, { name: p.name });
    if (i >= 0) s!.processes[i] = p; else s!.processes = [p, ...s!.processes];
    notify();
    return p;
  },
  removeProcess: async (id: string) => {
    load();
    await _op(approvalsDeleteProcess)?.(id);
    const prev = s!.processes.find((p) => p.id === id);
    if (prev) logDelete("approvalProcess", prev as unknown as Record<string, unknown>, { name: prev.name });
    s!.processes = s!.processes.filter((p) => p.id !== id);
    notify();
  },
  newProcess: (type?: string): ApprovalProcess => ({ id: apUid("ap"), type: type || "events", name: "", description: "", requireAll: true, status: "active", approvers: [] }),
  /* ── submissions ── */
  submission: (type: string, id?: string) => { load(); if (id === undefined) { id = type; type = "events"; } return s!.submissions[_key(type, id)] || null; },
  all: (type?: string) => { load(); return Object.values(s!.submissions).filter((v) => !type || v.type === type).map((v) => ({ ...v, eventId: v.itemId })); },
  pending: () => { load(); return Object.values(s!.submissions).filter((v) => v.status !== "approved" && v.status !== "draft").map((v) => ({ ...v, eventId: v.itemId })); },
  pendingCount: (type?: string) => { load(); return Object.values(s!.submissions).filter((v) => (!type || v.type === type) && v.status !== "approved" && v.status !== "rejected" && v.status !== "draft").length; },
  submitItem: async (type: string, itemId: string, processId: string, by?: string) => {
    load(); const now = "just now";
    const sub: ApprovalSubmission = { type, itemId, processId, status: "submitted", submittedBy: by || "You", submittedAt: now,
      history: [{ status: "submitted", at: now, by: by || "You" }], feedback: [] };
    try {
      await _op(approvalsSubmitForApproval)?.({ type, itemId, processId });
    } catch (e) {
      console.warn("approvalsSubmitForApproval failed:", e);
      toast({ tone: "danger", title: i18nT("approvals.store.couldnTSubmitForApproval", "Couldn't submit for approval"), subtitle: i18nT("approvals.store.nothingWasSubmittedRetryInAMoment", "Nothing was submitted. Retry in a moment.") });
      return null;
    }
    s!.submissions[_key(type, itemId)] = sub;
    _log({ type, itemId, action: `Submitted ${APPROVAL_TYPE(type).noun} for approval` });
    notify();
    toast({ tone: "success", title: i18nT("approvals.store.submittedForApproval", "Submitted for approval"), subtitle: i18nT("approvals.store.approversHaveBeenEmailedALinkToReview", "Approvers have been emailed a link to review.") });
    return sub;
  },
  submit: (eventId: string, processId: string, by?: string) => ApprovalsStore.submitItem("events", eventId, processId, by),
  addFeedback: async (type: string, id: string, fb: { by: string; text?: string; attachments?: { type: string; name: string }[]; decision?: string }) => {
    load();
    const sub = s!.submissions[_key(type, id)]; if (!sub) return;
    try {
      await _op(approvalsDecideSubmission)?.({ type, itemId: id, decision: fb.decision || "feedback", text: fb.text, attachments: fb.attachments, by: fb.by });
    } catch (e) {
      console.warn("approvalsDecideSubmission failed:", e);
      toast({ tone: "danger", title: i18nT("approvals.store.couldnTRecordTheDecision", "Couldn't record the decision"), subtitle: i18nT("approvals.store.theSubmissionIsUnchangedRetryInAMoment", "The submission is unchanged. Retry in a moment.") });
      return;
    }
    sub.feedback = sub.feedback || [];
    sub.feedback.unshift({ id: apUid("fb"), at: "just now", ...fb });
    if (fb.decision === "approved") sub.status = "approved";
    else if (fb.decision === "rejected") sub.status = "rejected";
    else sub.status = "feedback";
    sub.history = sub.history || [];
    sub.history.unshift({ status: sub.status, at: "just now", by: fb.by });
    const tm = APPROVAL_TYPE(type);
    if (tm.setStatus) { if (sub.status === "approved") tm.setStatus(id, "approved"); else if (sub.status === "rejected") tm.setStatus(id, "draft"); }
    const actionMap: Record<string, string> = { approved: `Approved ${tm.noun}`, rejected: `Rejected ${tm.noun}`, feedback: `Left feedback on ${tm.noun}` };
    _log({ type, itemId: id, action: actionMap[sub.status] || `Updated ${tm.noun}`, severity: sub.status === "rejected" ? "warn" : "info" });
    notify();
    const map: Record<string, string> = { approved: "Approved", rejected: "Rejected", feedback: "Feedback sent to submitter" };
    toast({ tone: fb.decision === "rejected" ? "warn" : "success", title: map[sub.status] || "Updated", subtitle: i18nT("approvals.store.theSubmitterHasBeenEmailedAnUpdate", "The submitter has been emailed an update.") });
    return sub;
  },
  setReviewing: async (type: string, id: string) => {
    load(); const sub = s!.submissions[_key(type, id)];
    if (!(sub && sub.status === "submitted")) return;
    try { await _op(approvalsMarkUnderReview)?.({ type, itemId: id }); } catch (e) { console.warn("approvalsMarkUnderReview failed:", e); return; }
    sub.status = "under-review"; sub.history.unshift({ status: "under-review", at: "just now" }); notify();
  },
  reset: async (type: string, id: string) => {
    load(); const k = _key(type, id);
    if (!s!.submissions[k]) return;
    try { await _op(approvalsResetSubmission)?.({ type, itemId: id }); } catch (e) { console.warn("approvalsResetSubmission failed:", e); toast({ tone: "danger", title: i18nT("approvals.store.couldnTResetSubmission", "Couldn't reset submission") }); return; }
    delete s!.submissions[k]; notify();
  },
  subscribe: (fn: () => void) => { subs.add(fn); return () => { subs.delete(fn); }; },
};
/* Coming soon: the same surface, answering "approvals are off" to every read and doing
   nothing on every write, so no caller needs its own check. */
if (APPROVALS_COMING_SOON) {
  const off = ApprovalsStore as unknown as Record<string, unknown>;
  Object.assign(off, {
    enabledTypes: () => Object.fromEntries(APPROVAL_TYPES.map((t) => [t.id, false])),
    isEnabled: () => false,
    enabled: () => false,
    anyEnabled: () => false,
    setEnabled: async () => {},
    isBlocked: () => false,
    blockReason: () => "",
    activity: () => [],
    auditEntries: () => [],
    processes: () => [],
    getProcess: () => null,
    upsertProcess: async (p: ApprovalProcess) => p,
    removeProcess: async () => {},
    submission: () => null,
    all: () => [],
    pending: () => [],
    pendingCount: () => 0,
    submitItem: async () => null,
    submit: async () => null,
    addFeedback: async () => undefined,
    setReviewing: async () => {},
    reset: async () => {},
  });
}

export function useApprovals() { const [, f] = useState(0); useEffect(() => ApprovalsStore.subscribe(() => f((n) => n + 1)), []); return ApprovalsStore; }

export const apInput = (extra: CSSProperties = {}): CSSProperties => ({ width: "100%", height: 40, padding: "0 12px", boxSizing: "border-box", border: `1px solid ${T.n4}`, borderRadius: 8, fontFamily: T.font, fontSize: 14, color: T.n1, background: T.white, outline: "none", ...extra });
export const apLabel: CSSProperties = { fontSize: 13, fontWeight: 600, color: T.n1, marginBottom: 6 };
export const apTh: CSSProperties = { textAlign: "left", padding: "10px 16px", whiteSpace: "nowrap" };
export const apTd: CSSProperties = { padding: "12px 16px", verticalAlign: "middle" };

export function ApprovalStatusPill({ status }: { status: string }) { const m = APPROVAL_STATUS[status] || APPROVAL_STATUS.draft; return <Pill tone={m.tone as any} dot={m.dot}>{m.label}</Pill>; }

const IconMap = Icon as unknown as Record<string, (p?: { size?: number }) => ReactNode>;

/* ─── Status timeline (shared by submitter + approver) ─────────────────── */
export function ApprovalTimeline({ status }: { status: string }) {
  const t = useT();
  const order = ["submitted", "under-review", "feedback", "approved"];
  const rejected = status === "rejected";
  const curIdx = status === "rejected" ? 2 : order.indexOf(status);
  return (
    <div style={{ minWidth: 0, overflowX: "auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 0, minWidth: "min-content" }}>
        {order.map((st, i) => {
          const m = APPROVAL_STATUS[st];
          const done = i <= curIdx && !rejected;
          const here = i === curIdx;
          const I = IconMap[m.icon] || IconMap.check;
          return (
            <React.Fragment key={st}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, minWidth: 76 }}>
                <span style={{ width: 30, height: 30, borderRadius: 999, display: "inline-flex", alignItems: "center", justifyContent: "center",
                  background: done ? T.green1 : here ? T.p1 : T.white, color: (done || here) ? T.white : T.n3,
                  border: (done || here) ? "none" : `1.5px solid ${T.n4}` }}>{I({ size: 14 })}</span>
                <span style={{ fontSize: 11.5, fontWeight: here ? 700 : 500, color: here ? T.n1 : T.soft, textAlign: "center" }}>{m.label}</span>
              </div>
              {i < order.length - 1 ? <span style={{ flex: 1, height: 2, background: i < curIdx && !rejected ? T.green1 : T.n4, minWidth: 16 }} /> : null}
            </React.Fragment>
          );
        })}
        {rejected ? <div style={{ marginLeft: 16, display: "inline-flex", alignItems: "center", gap: 6, color: T.red1, fontWeight: 700, fontSize: 13 }}><Icon.x size={14} /> {t("approvals.store.rejected", "Rejected")}</div> : null}
      </div>
    </div>
  );
}
